import { supabase } from '../lib/supabase';
import type { WeatherData } from '../types/weather';

const WEATHER_CACHE_KEY_PREFIX = 'raceprox_showcase_weather';
const WEATHER_CACHE_TTL_MS = 15 * 60 * 1000;

interface WeatherCachePayload {
  timestamp: number;
  payload: WeatherData;
}

interface ShowcaseWeatherResponse {
  data?: WeatherData;
  error?: string;
}

function cacheKey(latitude: number, longitude: number): string {
  return `${WEATHER_CACHE_KEY_PREFIX}:${latitude.toFixed(3)}:${longitude.toFixed(3)}`;
}

function readCachedWeather(latitude: number, longitude: number): WeatherData | null {
  try {
    const raw = localStorage.getItem(cacheKey(latitude, longitude));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as WeatherCachePayload;
    if (Date.now() - parsed.timestamp > WEATHER_CACHE_TTL_MS) {
      return null;
    }

    return parsed.payload;
  } catch {
    return null;
  }
}

function writeCachedWeather(latitude: number, longitude: number, payload: WeatherData): void {
  try {
    localStorage.setItem(
      cacheKey(latitude, longitude),
      JSON.stringify({
        timestamp: Date.now(),
        payload,
      } satisfies WeatherCachePayload),
    );
  } catch {
    // TV displays may run with storage disabled; cache is an optimization.
  }
}

export async function fetchShowcaseWeather(
  latitude: number,
  longitude: number,
): Promise<WeatherData> {
  const cached = readCachedWeather(latitude, longitude);
  if (cached) return cached;

  const { data, error } = await supabase.functions.invoke<ShowcaseWeatherResponse>(
    'showcase-weather',
    {
      body: {
        latitude,
        longitude,
        temperatureUnit: 'fahrenheit',
      },
    },
  );

  if (error) {
    throw new Error(error.message);
  }

  if (!data?.data) {
    throw new Error(data?.error || 'Weather data unavailable');
  }

  writeCachedWeather(latitude, longitude, data.data);
  return data.data;
}
